import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {EQOImageSize} from '@quickorder/angular-web-lib';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {MatSelect} from '@angular/material/select';

export interface IAutoCompleteData {
	id: number | string;
	value: string;
	imageUrl: string;
	letters: string;
	checked?: boolean;

}

export interface IAutoCompleteOptions {
	label: string;
	data: IAutoCompleteData[];
}

@Component({
	selector: 'app-autocomplete-multiselect',
	templateUrl: './autocomplete-multiselect.component.html',
	styleUrls: ['./autocomplete-multiselect.component.scss'],
})

export class AutocompleteMultiselectComponent implements OnInit, OnDestroy {
	@ViewChild('autocompleteMultiselectInput') public autoCompleteInput: ElementRef;
	@ViewChild('select') public select: MatSelect;
	@Input() public placeholder: string = 'To:';
	@Input() public imageSize: EQOImageSize = EQOImageSize.MEDIUM;

	@Output() public filterChanged: EventEmitter<IAutoCompleteOptions[]> = new EventEmitter();

	public _options: IAutoCompleteOptions[] = [];
	public filtered: IAutoCompleteOptions[] = [];
	public dataToReturn: IAutoCompleteOptions[] = [];

	public selectedValues: {
		id: number | string,
		value: string,
	}[] = [];

	public hoverNumber: number = 0;

	public form: FormGroup;

	private _destroy$: Subject<void> = new Subject<void>();

	@Input()
	public set options(options: IAutoCompleteOptions[]) {
		this._options = options;
		this.filterData();
	}

	constructor(
		private formBuilder: FormBuilder,
	) {
		this.initForm();
	}

	public get searchTermControl(): FormControl {
		return this.form.get('searchTerm') as FormControl;
	}

	public ngOnInit(): void {
		this.initSubscriptions();
	}

	public filterData(search = false): void {
		const searchTerm = this.searchTermControl.value.toLowerCase();
		const options = [...this._options];
		if (!search) {
			this.filtered = options.map(opt => ({
				...opt,
				data: opt.data.filter(s => this.selectedValues.map(value => value.id).indexOf(s.id) === -1).filter(d => d.value.includes(searchTerm)),
			})).filter(d => d.data.length > 0);
		} else {
			this.filtered = options.map(opt => ({
				...opt,
				data: opt.data.filter(d => d.value.toLowerCase().includes(searchTerm)),
			})).filter(d => d.data.length > 0);
		}

		this.dataToReturn = options.map(opt => ({
			...opt,
			data: opt.data.filter(s => this.selectedValues.map(value => value.id).indexOf(s.id) !== -1).filter(d => d.value.includes(searchTerm)),
		})).filter(d => d.data.length > 0);
	}

	public optionSelected(option: IAutoCompleteData): void {
		this.searchTermControl.patchValue('');
		const hoverNumberOnLastOption = this.filtered[0] && this.hoverNumber === this.filtered[0].data.length - 1;
		if (hoverNumberOnLastOption && this.hoverNumber !== 0) {
			this.hoverNumber--;
		}

		this.selectedValues.push({
			id: option.id,
			value: option.value,
		});
		this.filterData();
		this.filterChanged.emit(this.dataToReturn);
	}

	public removeValue(selectedValue: any): void {
		this.selectedValues.splice(this.selectedValues.map(value => value.id).indexOf(selectedValue.id), 1);
		this.filterData();
		this.filterChanged.emit(this.dataToReturn);
	}

	public selectFirstOption(): void {
		if (this.filtered[0] && this.filtered[0].data[this.hoverNumber]) {
			this.optionSelected(this.filtered[0].data[this.hoverNumber]);
		}
	}

	public moveHoverNumber(action: 'UP' | 'DOWN'): void {
		const onFirstOption = this.hoverNumber === 0;
		if (action === 'UP' && !onFirstOption) {
			this.hoverNumber--;
		}

		const onLastOption = this.filtered[0] && this.hoverNumber === this.filtered[0].data.length - 1;
		if (action === 'DOWN' && !onLastOption) {
			this.hoverNumber++;
		}
	}

	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	private initForm(): void {
		this.form = this.formBuilder.group({
			searchTerm: [''],
		});
	}

	private initSubscriptions(): void {
		this.searchTermControl.valueChanges
			.pipe(
				takeUntil(this._destroy$),
				distinctUntilChanged(),
				filter(searchTerm => !!searchTerm),
			)
			.subscribe(() => {
				this.filterData(true);
				this.select.open();
			});
	}
}
